import { Box, Card, CardContent, CardHeader, Stack } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { GridColumns } from '@/types/common';

import { LoadingIndicator } from '../LoadingIndicator';
import useStyles from './TableWidget.styles';
import type { TableWidgetProps } from './TableWidget.types';

const TableWidget = (props: TableWidgetProps) => {
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [tableWidth, setTableWidth] = useState<number>(10);
  const ref = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const {
    columns,
    rows,
    onRowClick,
    loading,
    title,
    header,
    pageSizeOptions,
    cellAlignment,
    headerAction,
    rowHeight,
    onCellClick,
    disableRowSelectionOnClick = false,
    customHeader,
    variant = 'default',
    customHeaderHeight,
    density = 'standard'
  } = props;
  const { classes } = useStyles({
    rowLength: rows.length,
    header,
    cellAlignment,
    rowClickable: !!onRowClick,
    cellClickable: !!onCellClick,
    variant
  });

  // Gets height for card header
  useEffect(() => {
    if (!ref.current) return;
    const resizeObserver = new ResizeObserver(() => {
      const element = ref.current;
      if (element) {
        const heightValue = element.offsetHeight;
        setHeaderHeight(heightValue);
      }
    });
    resizeObserver.observe(ref.current);
    return () => resizeObserver.disconnect();
  }, [ref]);

  // Gets width for table
  useEffect(() => {
    if (!cardRef.current) return;
    const resizeObserver = new ResizeObserver(
      debounce(() => {
        const element = cardRef.current;
        if (element) {
          const widthValue = element.offsetWidth;
          setTableWidth(widthValue);
        }
      }, 100)
    );
    resizeObserver.observe(cardRef.current);
    return () => resizeObserver.disconnect();
  }, [cardRef]);

  /**
   * @returns an object with all hidden columns, where value of key is equal to the isColumnVisible property of column (false if hidden)
   */
  const hiddenColumns = useMemo(() => {
    const hiddenColumnsArray: { [key: string]: boolean } = {};
    columns.forEach((column: GridColumns) => {
      if (column.isColumnVisible === false) {
        hiddenColumnsArray[column.field] = column.isColumnVisible;
      }
    });
    return hiddenColumnsArray;
  }, [columns]);

  const noRowsDisplay = useMemo(() => {
    return (
      <Stack
        sx={{
          color: 'text.secondary',
          paddingBottom: header ? '15px' : '100px',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        No data
      </Stack>
    );
  }, []);

  return (
    <Card className={classes.tableCard}>
      {(!header || variant === 'reports') && (
        <CardHeader
          ref={ref}
          slotProps={{ title: { variant: 'body1' } }}
          className={classes.tableCardHeader}
          title={title}
          action={headerAction}
        />
      )}
      <CardContent
        ref={cardRef}
        style={{ height: `calc(100% - ${headerHeight}px)` }}
        className={classes.tableCardContent}
      >
        {!customHeader && !rows.length && !loading && (
          <Box
            sx={{
              height: '100%',
              color: 'text.secondary',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            No data
          </Box>
        )}
        {(loading || tableWidth === 10) && (
          <div key="loading-indicator" className={classes.cardLoading}>
            <LoadingIndicator noBackground isLoading={loading} />
          </div>
        )}
        {tableWidth > 10 && !loading && (
          <DataGrid
            style={{ width: tableWidth, height: '100%' }}
            getRowHeight={() => rowHeight || 'auto'}
            density={density}
            onRowClick={onRowClick}
            onCellClick={onCellClick}
            className={classes.tableWidget}
            rows={rows}
            columns={columns}
            hideFooter={!pageSizeOptions}
            disableColumnSelector
            pageSizeOptions={pageSizeOptions}
            disableRowSelectionOnClick={disableRowSelectionOnClick}
            columnHeaderHeight={header ? customHeaderHeight : 0}
            slots={{
              noRowsOverlay: () => noRowsDisplay,
              toolbar: customHeader
            }}
            initialState={{
              columns: {
                columnVisibilityModel: hiddenColumns
              }
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

TableWidget.displayName = 'TableWidget';

export default TableWidget;
